import _ from 'lodash'
import { hasAuthParams } from 'react-oidc-context'
import { handleExceptPath } from 'src/events'
import { clearFileTicket } from './fileTicket'

const IN_APP_KEY = 'isInApp'
const CHECKED_PARAM = 'ssoChecked'
const OIDC_STATE_PREFIX = 'oidc.'
const KEPT_ON_RESET = ['classUuid', IN_APP_KEY]
export const SSO_SUB_KEY = 'ssoSub'
export const SSO_CHECK_PATH = '/sso-check'

export const AGENT_SSO = {
  CALLBACK: 'callback',
  CHECK: 'check',
  LOGIN: 'login',
}

const toPath = ({ pathname, search, hash }) => `${pathname}${search}${hash}`

const isInIframe = () => {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

const isInApp = () =>
  isInIframe() ||
  new URLSearchParams(window.location.search).get(IN_APP_KEY) === 'true'

const isAgentWithSession = () =>
  localStorage.getItem('type') === 'Agent' &&
  !_.isEmpty(localStorage.getItem('user'))

const isCheckRoute = () => window.location.pathname === SSO_CHECK_PATH

const getCheckRouteReturnTo = () => {
  const returnTo = new URLSearchParams(window.location.search).get('returnTo')
  return returnTo?.startsWith('/') && !returnTo.startsWith('//')
    ? returnTo
    : '/'
}

const markChecked = (path) => {
  const url = new URL(path, window.location.origin)
  url.searchParams.set(CHECKED_PARAM, '1')
  return toPath(url)
}

const consumeCheckedMark = () => {
  const url = new URL(window.location.href)
  if (!url.searchParams.has(CHECKED_PARAM)) return false
  url.searchParams.delete(CHECKED_PARAM)
  window.history.replaceState(window.history.state, '', toPath(url))
  return true
}

const readCallbackState = () => {
  const state = new URLSearchParams(window.location.search).get('state')
  try {
    const stored = JSON.parse(localStorage.getItem(OIDC_STATE_PREFIX + state))
    return stored?.data?.agentSso ? stored.data : null
  } catch {
    return null
  }
}

export const resolveAgentSsoAction = () => {
  if (hasAuthParams()) {
    const callback = readCallbackState()
    return callback
      ? { type: AGENT_SSO.CALLBACK, returnTo: callback.returnTo }
      : null
  }
  if (consumeCheckedMark()) return null

  const checkRoute = isCheckRoute()
  if (!checkRoute && handleExceptPath(window.location)) return null

  const returnTo = checkRoute
    ? getCheckRouteReturnTo()
    : toPath(window.location)
  if (isAgentWithSession()) return { type: AGENT_SSO.CHECK, returnTo }
  if (checkRoute || isInApp()) return { type: AGENT_SSO.LOGIN, returnTo }
  return null
}

const resetAgentSession = (returnTo) => {
  const kept = KEPT_ON_RESET.map((key) => [key, localStorage.getItem(key)])
  clearFileTicket()
  localStorage.clear()
  kept.forEach(([key, value]) => value && localStorage.setItem(key, value))
  localStorage.setItem('type', 'Agent')
  localStorage.setItem('previousPath', markChecked(returnTo))
}

export const startAgentSsoCheck = (auth, { returnTo }) => {
  auth.signinRedirect({
    prompt: 'none',
    redirect_uri: window.__env__.REACT_APP_APP_URL,
    state: { agentSso: true, returnTo },
  })
}

export const startAgentLogin = ({ returnTo }) => {
  resetAgentSession(returnTo)
  window.location.replace('/logging-in')
}

const getAgentToken = (authorization) => {
  const token =
    window.__env__.ENV !== 'DEV'
      ? authorization.id_token
      : authorization.access_token
  return `${authorization.token_type} ${token}`
}

const isOtherApp = (path) => path.startsWith('/dms')

export const handleAgentSsoUser = (authorization, { returnTo }) => {
  const sub = _.get(authorization, 'profile.sub', '')
  const token = getAgentToken(authorization)

  if (sub && sub === localStorage.getItem(SSO_SUB_KEY)) {
    localStorage.setItem('refresh_token', authorization.refresh_token)
    localStorage.setItem('token', token)
    if (isOtherApp(returnTo)) {
      window.location.replace(markChecked(returnTo))
      return false
    }
    window.history.replaceState(null, '', returnTo)
    return true
  }

  resetAgentSession(returnTo)
  localStorage.setItem(SSO_SUB_KEY, sub)
  localStorage.setItem('refresh_token', authorization.refresh_token)
  localStorage.setItem('token', token)
  window.location = '/waiting-for-login'
  return false
}

export const handleAgentSsoError = ({ returnTo }) => {
  window.location.replace(markChecked(returnTo))
}
